import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { FadeUp, RevealLine } from "@/components/Reveal";
import { motion } from "framer-motion";
import { ShieldOff, Cpu, Signal, Fingerprint, Package, Terminal, ArrowUpRight, FileCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SPECS = [
  {
    n: "01",
    icon: Terminal,
    title: "Kernel-level hardening",
    body:
      "The stock Google kernel is replaced with a GrapheneOS build compiled with our internal hardening flags: CFI (Control-Flow Integrity) forced, KASLR entropy widened, hardened_malloc replacing the default allocator, and unused syscalls masked. The build tag is signed and reproducible against our public checksum registry.",
    bullets: [
      "hardened_malloc + slab quarantine",
      "Full CFI on every executable",
      "KASLR + KPTI on every boot",
      "Zeroed pages on free()",
      "Syscall filter (seccomp) per app",
    ],
  },
  {
    n: "02",
    icon: Cpu,
    title: "Proprietary blob excision",
    body:
      "Every closed-source binary that shipped from Google is audited. Where a replacement exists we swap it (Fossify, Aurora, Molly). Where it doesn't, we neuter the blob's network privileges and route its telemetry sockets to /dev/null. The result: no undocumented radio can phone home.",
    bullets: [
      "Play Services · removed",
      "Play Store · replaced (F-Droid + Aurora)",
      "Play Protect · disabled",
      "GPS assist blobs · sandboxed",
      "OEM diagnostic partitions · read-only",
    ],
  },
  {
    n: "03",
    icon: Signal,
    title: "Encrypted eSIMs",
    body:
      "Each Phantom device is configured with two eSIM slots: a primary provisioned to a carrier of your choice, and a secondary quarantine profile that routes all data through a WireGuard tunnel with kill-switch. We provide printed activation cards; the eSIM profile is delivered by encrypted courier or PGP-signed email.",
    bullets: [
      "Two eSIM slots per device",
      "Primary + quarantine profiles",
      "Kill-switch on secondary APN",
      "IMSI catcher detector installed",
      "Carrier-agnostic (BYO or ours)",
    ],
  },
  {
    n: "04",
    icon: ShieldOff,
    title: "Physical hardware modifications",
    body:
      "For the operator who cannot tolerate a single acoustic leak: primary microphone modules physically disconnected at our facility, USB data pins severed (power still works), and location services blocked at the sensor bus. Bluetooth is retained for accessory use only.",
    bullets: [
      "Mic module physical disconnect",
      "USB data pin severed (power only)",
      "Location bus hardware-blocked",
      "Speaker retained (playback still works)",
      "Bluetooth accessory-scope only",
    ],
  },
  {
    n: "05",
    icon: Fingerprint,
    title: "Encrypted app suite",
    body:
      "Every Phantom ships with a curated toolkit installed and configured. Nothing bolted on. Nothing left to chance.",
    bullets: [
      "Molly · hardened Signal fork",
      "Session · metadata-free messaging",
      "Aegis · offline 2FA",
      "OnionShare · Tor-native file share",
      "KeePassDX · encrypted vault",
      "Mullvad Browser · fingerprint-resistant",
      "Orbot · Tor for all apps",
    ],
  },
  {
    n: "06",
    icon: Package,
    title: "Tamper-evident chain of custody",
    body:
      "Every device receives a serialised seal number. Screws are lacquered with glitter nail polish; a macro photograph is taken and delivered to you before dispatch. Sealed in a Faraday bag inside a holographic tamper-evident pouch. Any mismatch on arrival = do not open. Contact us immediately.",
    bullets: [
      "Serialised seal number per unit",
      "Glitter-lacquer on every screw",
      "Macro-photo verified before ship",
      "Faraday + holographic pouch",
      "Signature-on-delivery only",
    ],
  },
];

export default function Hardening() {
  const [manifest, setManifest] = useState(null);
  const [manifestErr, setManifestErr] = useState(false);
  const [ascStatus, setAscStatus] = useState("idle"); // idle | present | absent | checking
  useEffect(() => {
    api.get("/verify/manifest")
      .then((r) => setManifest(r.data))
      .catch(() => setManifestErr(true));
    // HEAD-style probe for the signed envelope
    setAscStatus("checking");
    api.get("/verify/manifest.asc", { transformResponse: [(d) => d] })
      .then(() => setAscStatus("present"))
      .catch(() => setAscStatus("absent"));
  }, []);
  return (
    <div className="pt-32 pb-24 px-6 md:px-10" data-testid="hardening-page">
      <div className="overline mb-6 text-[#7C3AED]">Ξ AUDIT DOCUMENT — CLASSIFIED PHANTOM</div>
      <h1 className="font-display uppercase text-6xl md:text-[8vw] tracking-tight leading-[0.9]">
        <RevealLine i={0}>The Phantom</RevealLine>
        <RevealLine i={1}>hardening protocol.</RevealLine>
      </h1>

      <FadeUp delay={0.2}>
        <div className="mt-12 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <p className="text-[#888] text-lg leading-relaxed max-w-3xl">
              Every Phantom-tier device passes through a seven-station audit before it leaves our
              Sydney facility. This document details each station — the kernel work, the blob
              excision, the eSIM plumbing, the physical modifications, the app suite, the
              tamper-evident shipping chain, and finally the reproducible-build verification
              anyone can perform themselves. Nothing on this page is marketing. Every claim
              corresponds to a checklist item in our internal QA workflow.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <div className="border border-[#7C3AED]/40 p-5">
              <div className="overline text-[#7C3AED]">Verify</div>
              <div className="text-[#EAEAEA] text-sm mt-2">
                Every Phantom order is delivered with a printed audit sheet signed and dated by the
                engineer who did the work, referencing the checklist below.
              </div>
              <div className="mt-4 font-mono text-xs text-[#666]">DOCUMENT REV · PHM-2026.Ξ.001</div>
            </div>
          </div>
        </div>
      </FadeUp>

      {/* Sections */}
      <div className="mt-24 space-y-24 md:space-y-32">
        {SPECS.map((s, i) => {
          const Icon = s.icon;
          return (
            <FadeUp key={s.n}>
              <div className="grid grid-cols-12 gap-6 md:gap-10 items-start border-t border-[#1A1A1A] pt-16">
                <div className="col-span-12 md:col-span-3">
                  <div className="overline text-[#666]">STATION</div>
                  <div className="font-display text-[7rem] md:text-[9rem] leading-none text-[#222] mt-2">{s.n}</div>
                  <Icon size={28} className="text-[#7C3AED] mt-4" strokeWidth={1.2} />
                </div>
                <div className="col-span-12 md:col-span-9">
                  <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95]">{s.title}</h2>
                  <p className="mt-8 text-[#888] text-base md:text-lg leading-relaxed max-w-3xl">{s.body}</p>
                  <div className="mt-8 border-t border-[#1A1A1A] pt-6">
                    <div className="overline mb-4 text-[#666]">Checklist</div>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 font-mono text-sm">
                      {s.bullets.map((b, j) => (
                        <li key={b} className="flex items-start gap-3 text-[#EAEAEA]">
                          <span className="text-[#7C3AED] font-mono text-xs mt-0.5">[{String(j + 1).padStart(2, "0")}]</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </FadeUp>
          );
        })}

        {/* Station 07 — Reproducible-build verification */}
        <FadeUp>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-start border-t border-[#1A1A1A] pt-16" data-testid="hardening-station-07">
            <div className="col-span-12 md:col-span-3">
              <div className="overline text-[#666]">STATION</div>
              <div className="font-display text-[7rem] md:text-[9rem] leading-none text-[#222] mt-2">07</div>
              <FileCheck size={28} className="text-[#7C3AED] mt-4" strokeWidth={1.2} />
            </div>
            <div className="col-span-12 md:col-span-9">
              <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-[0.95]">Reproducible-build verification</h2>
              <p className="mt-8 text-[#888] text-base md:text-lg leading-relaxed max-w-3xl">
                Every Phantom-tier release ships as a byte-for-byte reproducible build. We publish a
                signed manifest containing SHA-256 hashes for <code className="text-[#EAEAEA] font-mono">boot.img</code>,
                <code className="text-[#EAEAEA] font-mono"> system.img</code> and
                <code className="text-[#EAEAEA] font-mono"> vendor.img</code> for each device. Anyone
                can pull the manifest, rebuild GrapheneOS from source with our published patch stack,
                and confirm the hashes match. If they don't — don't flash the device. Contact us.
              </p>

              {/* Manifest header */}
              <div className="mt-8 border border-[#1A1A1A] p-5" data-testid="verify-manifest-header">
                <div className="flex flex-wrap items-baseline justify-between gap-4">
                  <div>
                    <div className="overline text-[#7C3AED]">Current manifest</div>
                    <div className="mt-1 font-mono text-sm text-[#EAEAEA]" data-testid="verify-build-tag">
                      {manifestErr ? "manifest unavailable" : (manifest?.build_tag || "loading…")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="overline text-[#666]">GrapheneOS release</div>
                    <div className="mt-1 font-mono text-sm text-[#EAEAEA]">{manifest?.graphene_release || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="overline text-[#666]">Signed by</div>
                    <div className="mt-1 font-mono text-[10px] text-[#EAEAEA] break-all max-w-[24ch]">{manifest?.signed_by || "—"}</div>
                  </div>
                </div>

                {/* Hash table */}
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-xs font-mono" data-testid="verify-hash-table">
                    <thead>
                      <tr className="border-b border-[#1A1A1A]">
                        <th className="p-2 text-left overline text-[#666]">Device</th>
                        <th className="p-2 text-left overline text-[#666]">Image</th>
                        <th className="p-2 text-left overline text-[#666]">SHA-256</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(manifest?.artifacts || []).flatMap((a) =>
                        a.images.map((img) => (
                          <tr key={a.device + img.file} className="border-b border-[#1A1A1A]/60">
                            <td className="p-2 text-[#EAEAEA]">{a.device}</td>
                            <td className="p-2 text-[#EAEAEA]">{img.file}</td>
                            <td className="p-2 text-[#7C3AED] break-all">{img.sha256}</td>
                          </tr>
                        ))
                      )}
                      {!manifest && !manifestErr && (
                        <tr><td colSpan={3} className="p-3 text-[#666]">Loading manifest…</td></tr>
                      )}
                      {manifestErr && (
                        <tr><td colSpan={3} className="p-3 text-[#666]">Manifest endpoint unreachable — see /api/verify/manifest.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href="/api/verify/manifest"
                    target="_blank" rel="noopener noreferrer"
                    className="btn-ghost text-[10px]"
                    data-testid="verify-fetch-manifest"
                  >Fetch raw manifest →</a>
                  <button
                    className="btn-ghost text-[10px]"
                    onClick={() => { navigator.clipboard.writeText(JSON.stringify(manifest, null, 2)); toast.success("Manifest copied"); }}
                    disabled={!manifest}
                    data-testid="verify-copy-manifest"
                  >Copy JSON →</button>
                  <a
                    href="/api/verify/manifest.asc"
                    target="_blank" rel="noopener noreferrer"
                    className={`btn-ghost text-[10px] ${ascStatus !== "present" ? "opacity-40 pointer-events-none" : ""}`}
                    data-testid="verify-signature"
                    aria-disabled={ascStatus !== "present"}
                    title={ascStatus === "present"
                      ? "Open the clearsigned envelope — run gpg --verify locally against fingerprint 0977 1DBB … BCC2"
                      : "No clearsigned envelope has been published for this release yet."}
                  >{ascStatus === "present" ? "Verify signature (.asc) →" : "Signature pending"}</a>
                </div>
              </div>

              {/* CLI snippet */}
              <div className="mt-6 border border-[#1A1A1A] p-5" data-testid="verify-cli-snippet">
                <div className="overline text-[#666] mb-3">Verify locally — 30 seconds</div>
                <pre className="bg-[#050505] border border-[#1A1A1A] p-4 text-[11px] md:text-xs text-[#EAEAEA] overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">
{`# 1. Pull the signed manifest + its clearsigned envelope
curl -s https://phantomphone.info/api/verify/manifest      > phantom.json
curl -s https://phantomphone.info/api/verify/manifest.asc  > phantom.asc

# 2. Verify the signature against the admin key (fingerprint 0977 1DBB … BCC2)
gpg --verify phantom.asc

# 3. Extract the images off your device (or use the ones we ship on the USB stick)
adb pull /dev/block/by-name/boot   boot.img
adb pull /dev/block/by-name/system system.img
adb pull /dev/block/by-name/vendor vendor.img

# 4. Compare
sha256sum boot.img system.img vendor.img
# expected hashes are listed in phantom.json → artifacts[device].images[].sha256

# 5. Any mismatch = your device has been tampered with. Do not use. Contact admin@phantomphone.info.`}
                </pre>
              </div>

              <div className="mt-6 border-t border-[#1A1A1A] pt-6">
                <div className="overline mb-4 text-[#666]">Checklist</div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 font-mono text-sm">
                  {[
                    "Reproducible-build toolchain pinned per release",
                    "Public GrapheneOS patch stack (git-verifiable)",
                    "SHA-256 per image, per device, per release",
                    "Manifest clearsigned with admin PGP key",
                    "Printed hash card enclosed with every unit",
                    "Public build changelog with prior tags",
                  ].map((b, j) => (
                    <li key={b} className="flex items-start gap-3 text-[#EAEAEA]">
                      <span className="text-[#7C3AED] font-mono text-xs mt-0.5">[{String(j + 1).padStart(2, "0")}]</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>

      {/* Signed audit */}
      <FadeUp>
        <div className="mt-32 border-t border-[#7C3AED]/40 pt-16">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-6">
              <div className="overline text-[#666]">— END OF DOCUMENT —</div>
              <h2 className="font-display uppercase text-5xl md:text-7xl mt-6 leading-[0.9]">
                Every Phantom<br />
                <span className="text-[#7C3AED]">is audited.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 flex flex-col justify-end">
              <div className="border border-[#1A1A1A] p-6">
                <div className="overline">Chief engineer</div>
                <div className="font-display text-2xl mt-2">D. Marchetti</div>
                <div className="mt-2 text-[#888] text-sm">Sydney · signed on delivery of every Phantom-tier device.</div>
              </div>
              <div className="mt-6 flex gap-4">
                <Link to="/shop?tier=phantom" className="btn-vault danger" data-testid="hardening-shop-cta">
                  Browse Phantom devices <ArrowUpRight size={14} />
                </Link>
                <Link to="/contact" className="btn-ghost">Talk to us →</Link>
              </div>
            </div>
          </div>
        </div>
      </FadeUp>
    </div>
  );
}
